import { db } from "@/lib/firebase"
import { doc, updateDoc, arrayUnion, arrayRemove, getDoc, Timestamp } from "firebase/firestore"

/**
 * Follow an artist by adding their ID to the user's following array and userId to artist's followers array
 */
export async function followArtist(artistId: string, userId: string): Promise<void> {
  try {
    const followTimestamp = Timestamp.now()
    const userRef = doc(db, "users", userId)
    const artistRef = doc(db, "artists", artistId)
    
    // Add to user's following array
    await updateDoc(userRef, {
      following: arrayUnion({
        artistId: artistId,
        followedAt: followTimestamp
      })
    })
    
    // Add to artist's followers array
    await updateDoc(artistRef, {
      followers: arrayUnion({
        userId: userId,
        followedAt: followTimestamp
      })
    })
    
    console.log(`[followArtist] Successfully followed artist ${artistId}`)
  } catch (err) {
    console.error('[followArtist] Error:', err)
    throw err
  }
}

/**
 * Unfollow an artist by removing their ID from the user's following array and userId from artist's followers array
 */
export async function unfollowArtist(artistId: string, userId: string): Promise<void> {
  try {
    const userRef = doc(db, "users", userId)
    const artistRef = doc(db, "artists", artistId)
    
    // Get current user data to find the exact object to remove
    const userDoc = await getDoc(userRef)
    if (userDoc.exists()) {
      const userData = userDoc.data()
      const following = userData.following || []
      const followEntry = following.find((f: any) => f.artistId === artistId)
      
      if (followEntry) {
        await updateDoc(userRef, {
          following: arrayRemove(followEntry)
        })
      }
    }
    
    // Get current artist data to find the exact object to remove
    const artistDoc = await getDoc(artistRef)
    if (artistDoc.exists()) {
      const artistData = artistDoc.data()
      const followers = artistData.followers || []
      const followerEntry = followers.find((f: any) => f.userId === userId)
      
      if (followerEntry) {
        await updateDoc(artistRef, {
          followers: arrayRemove(followerEntry)
        })
      }
    }
    
    console.log(`[unfollowArtist] Successfully unfollowed artist ${artistId}`)
  } catch (err) {
    console.error('[unfollowArtist] Error:', err)
    throw err
  }
}

/**
 * Check if a user is following a specific artist
 */
export async function isFollowingArtist(artistId: string, userId: string): Promise<boolean> {
  try {
    const userRef = doc(db, "users", userId)
    const userDoc = await getDoc(userRef)
    
    if (!userDoc.exists()) {
      return false
    }
    
    const userData = userDoc.data()
    const following = userData.following || []
    return following.some((f: any) => f.artistId === artistId)
  } catch (err) {
    console.error('[isFollowingArtist] Error:', err)
    return false
  }
}

/**
 * Get the count of artists that a user is following (before adding a new follow)
 */
export async function getUserFollowingCount(userId: string): Promise<number> {
  try {
    const userRef = doc(db, "users", userId)
    const userDoc = await getDoc(userRef)
    
    if (!userDoc.exists()) {
      return 0
    }
    
    const userData = userDoc.data()
    const following = userData.following || []
    
    return following.length
  } catch (err) {
    console.error('[getUserFollowingCount] Error:', err)
    return 0
  }
}

/**
 * Get all artists that a user is following
 */
export async function getUserFollowedArtists(userId: string): Promise<Array<{ artistId: string; followedAt: Date }>> {
  try {
    const userRef = doc(db, "users", userId)
    const userDoc = await getDoc(userRef)
    
    if (!userDoc.exists()) {
      return []
    }
    
    const userData = userDoc.data()
    const following = userData.following || []
    
    // Convert Firestore Timestamps to Dates
    return following.map((f: any) => ({
      artistId: f.artistId,
      followedAt: f.followedAt instanceof Timestamp ? f.followedAt.toDate() : new Date(f.followedAt)
    }))
  } catch (err) {
    console.error('[getUserFollowedArtists] Error:', err)
    return []
  }
}

/**
 * Get all followers of an artist
 */
export async function getArtistFollowers(artistId: string): Promise<Array<{ userId: string; followedAt: Date }>> {
  try {
    const artistRef = doc(db, "artists", artistId)
    const artistDoc = await getDoc(artistRef)
    
    if (!artistDoc.exists()) {
      return []
    }
    
    const artistData = artistDoc.data()
    const followers = artistData.followers || []
    
    // Convert Firestore Timestamps to Dates
    return followers.map((f: any) => ({
      userId: f.userId,
      followedAt: f.followedAt instanceof Timestamp ? f.followedAt.toDate() : new Date(f.followedAt)
    }))
  } catch (err) {
    console.error('[getArtistFollowers] Error:', err)
    return []
  }
}
